import { Component, inject, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { NGXLogger } from 'ngx-logger';
import { delay, tap } from 'rxjs';
import { AppExceptionModel } from '../../../models/app-exception.model';
import { ProtestantCanonBooks, getBibleBooksCanon } from '../../../models/books.bible.model';
import { Store } from '@ngrx/store';
import { AppState } from '../../../state/appstate';
import * as groupSelector from '../../../state/selectors/group.selector';
import { QuizQuestion } from '../../../models/question.quiz.model';
import { QuizService } from '../../../services/quiz.service';
import { UtilsService } from '../../../services/utils.service';
import { BtnActionComponent } from '../../components/buttons/btn-action/btn-action.component';
import { BtnSubmitComponent } from '../../components/buttons/btn-submit/btn-submit.component';
import { BibleReferenceRangeFormComponent } from '../../components/inputs/bible-reference-range-form/bible-reference-range-form.component';

@Component({
  selector: 'app-question-search',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    BtnSubmitComponent,
    BtnActionComponent,
    BibleReferenceRangeFormComponent,
  ],
  templateUrl: './question-search.component.html',
  styleUrl: './question-search.component.css',
})
export class QuestionSearchComponent implements OnInit {
  @Input() groupId: string | null = null;
  @Input() showTagsFilter: boolean = true;
  @Input() showTextFilter: boolean = true;

  @Output() searchResults = new EventEmitter<QuizQuestion[]>();
  @Output() searchCleared = new EventEmitter<void>();
  @Output() loading = new EventEmitter<boolean>();

  searchForm: FormGroup;
  isLoading = false;
  bibleBooks = ProtestantCanonBooks as Record<string, string>;

  private logger = inject(NGXLogger);
  private quizService = inject(QuizService);
  private utilsService = inject(UtilsService);
  private fb = inject(FormBuilder);
  private store = inject(Store<AppState>);

  constructor() {
    this.searchForm = this.fb.group({
      reference: [null],
      text: [''],
      tags: [''],
    });
  }

  ngOnInit(): void {
    if (this.groupId) {
      this.store.select(groupSelector.getGroupById(this.groupId))
        .subscribe((group) => {
          this.bibleBooks = getBibleBooksCanon(group?.bibleType);
        });
    }
  }

  async searchQuestions() {
    this.logger.debug(
      'Searching questions in component with filters: ',
      JSON.stringify(this.searchForm.value),
    );

    if (!this.groupId || this.searchForm.invalid) {
      this.logger.error(
        'Group ID is null or form is invalid. Cannot search questions.',
      );
      return;
    }

    this.isLoading = true;
    this.loading.emit(true);

    try {
      const formVal = this.searchForm.value;
      const tagsArray = formVal.tags && this.showTagsFilter
        ? formVal.tags.split(',')
            .map((t: string) => t.trim().toLowerCase())
            .filter(Boolean)
        : [];

      const searchParams = {
        reference: formVal.reference && formVal.reference.book ? formVal.reference : null,
        text: this.showTextFilter ? (formVal.text || '') : '',
        tags: tagsArray,
      };

      this.quizService.getQuestions(this.groupId, searchParams)
        .pipe(
          delay(0),
          tap({
            next: (questions) => {
              this.isLoading = false;
              this.loading.emit(false);
              this.searchResults.emit(questions);
            },
            error: (err) => {
              this.isLoading = false;
              this.loading.emit(false);
              this.logger.error('Error fetching questions: ', err);
              this.utilsService.onShowAlert({
                code: 'FETCH_QUESTIONS_ERROR',
                title: 'Error Fetching Questions',
                message: 'Failed to load questions. Please try again later.',
              } as AppExceptionModel);
            }
          })
        )
        .subscribe();
    } catch (error) {
      this.isLoading = false;
      this.loading.emit(false);
      this.logger.error('Error in search configuration: ', error);
    }
  }

  clearSearch() {
    this.searchForm.reset({
      reference: null,
      text: '',
      tags: '',
    });
    this.searchCleared.emit();
    this.searchQuestions();
  }
}
